/**
 * The shape `/api/staff/dashboard/alerts` sends, and how a strip is arranged
 * from it (REQ-021 AC-007).
 *
 * As with `metrics.ts`, nothing is defined here that the backend defines. No
 * condition is named, no threshold is written down, and no work-queue route is
 * spelled out: the seven conditions, their owners and their links all arrive in
 * the payload, because `alerts.ts` on the server closes that list at module
 * load and a second copy on this side would be a list that can disagree with
 * the one being enforced.
 *
 * The one rule this file adds is the arrangement. Firing and unevaluable
 * conditions are each carried through individually; only `clear` is allowed to
 * collapse into a count. That asymmetry is the requirement: a condition nobody
 * can evaluate is not a quiet condition, and four of the seven are unevaluable
 * today, so a strip that summarised them alongside the clear ones would report
 * the exact silence AC-007 and AC-008 exist to break.
 */

export type AlertStatus = "firing" | "clear" | "unevaluable";

/** `AlertOutcome` as the route sends it — one condition's current standing. */
export interface AlertOutcome {
  condition: string;
  label: string;
  question: string;
  status: AlertStatus;
  severity: "warning" | "critical";
  owner: string;
  /** Absent where no queue in this app resolves the alert; see `workQueueGap`. */
  workQueue?: string;
  /** The backend's words for why there is no queue to link at. */
  workQueueGap?: string;
  message?: string;
  currentValue?: number;
  threshold?: number;
  reason?: string;
  metricId?: string;
}

export interface AlertsPayload {
  alerts: AlertOutcome[];
}

export interface AlertsMeta {
  computedAt: string;
  count: number;
  conditionLimit: number;
  firing: number;
  unevaluable: number;
}

export interface AlertPartition {
  firing: AlertOutcome[];
  unevaluable: AlertOutcome[];
  clear: AlertOutcome[];
}

/**
 * The three groups, with the firing ones worst-first.
 *
 * Within a status the backend's declaration order is kept otherwise, so the
 * strip reads in the order AC-007 lists the conditions rather than in an order
 * this file invented.
 */
export function partitionAlerts(alerts: AlertOutcome[]): AlertPartition {
  const firing = alerts
    .filter((alert) => alert.status === "firing")
    .sort(
      (a, b) =>
        Number(b.severity === "critical") - Number(a.severity === "critical"),
    );

  return {
    firing,
    unevaluable: alerts.filter((alert) => alert.status === "unevaluable"),
    clear: alerts.filter((alert) => alert.status === "clear"),
  };
}

/** "Critical" / "Warning", as a reader would say it. */
export const severityLabel = (severity: AlertOutcome["severity"]): string =>
  severity === "critical" ? "Critical" : "Warning";
